/** The result of one file in an upload batch. The portal shows one line for each file. */
export interface UploadResult {
  fileName: string
  ok: boolean
  message: string
}

export interface UploadSteps<F extends { name: string }> {
  /** Asks the API for a presigned URL. The API also records the file. */
  presign: (file: F) => Promise<{ attachmentId: string; uploadUrl: string }>
  /** Sends the file to storage. Returns `false` for an HTTP error. Throws for a network error. */
  put: (uploadUrl: string, file: F) => Promise<boolean>
  /** Asks the API if storage has the file of the recorded attachment. */
  exists: (attachmentId: string) => Promise<boolean>
  /** Removes the record of a file that did not reach storage. */
  discard: (attachmentId: string) => Promise<void>
}

export const UPLOADED = 'Uploaded'
export const NOT_STORED = 'The file did not reach storage. Try again.'
export const NOT_CONFIRMED = 'We could not confirm this upload. Reload the page to see the attached files.'
const FALLBACK = 'This file could not be uploaded. Try again.'

function messageOf(err: unknown): string {
  return err instanceof Error && err.message ? err.message : FALLBACK
}

async function uploadOne<F extends { name: string }>(file: F, steps: UploadSteps<F>): Promise<UploadResult> {
  let presigned: { attachmentId: string; uploadUrl: string }
  try {
    presigned = await steps.presign(file)
  } catch (err) {
    return { fileName: file.name, ok: false, message: messageOf(err) }
  }

  let putOk = false
  try {
    putOk = await steps.put(presigned.uploadUrl, file)
  } catch {
    // The browser gives "Failed to fetch" also when storage kept the file but the
    // response had no CORS headers. The check below decides.
  }
  if (putOk) return { fileName: file.name, ok: true, message: UPLOADED }

  let stored: boolean
  try {
    stored = await steps.exists(presigned.attachmentId)
  } catch {
    return { fileName: file.name, ok: false, message: NOT_CONFIRMED }
  }
  if (stored) return { fileName: file.name, ok: true, message: UPLOADED }

  try {
    await steps.discard(presigned.attachmentId)
  } catch {
    // The row stays in the list with its scan state. The contact can remove it.
  }
  return { fileName: file.name, ok: false, message: NOT_STORED }
}

/**
 * Uploads each file in turn. A failure on one file does not stop the others. `onResult`
 * gets each result as soon as it is known.
 */
export async function uploadFiles<F extends { name: string }>(
  files: readonly F[],
  steps: UploadSteps<F>,
  onResult: (result: UploadResult) => void = () => {},
): Promise<UploadResult[]> {
  const results: UploadResult[] = []
  for (const file of files) {
    const result = await uploadOne(file, steps)
    results.push(result)
    onResult(result)
  }
  return results
}

export const NOT_REMOVED = 'This file could not be removed. Try again.'

/**
 * Asks the API to remove an attached file. Returns `null` when the API removed it.
 * Otherwise returns the message to show in the row of the file, which is the message of
 * the API when it has one.
 */
export async function removeAttachment(
  attachmentId: string,
  remove: (attachmentId: string) => Promise<void>,
): Promise<string | null> {
  try {
    await remove(attachmentId)
    return null
  } catch (err) {
    return err instanceof Error && err.message ? err.message : NOT_REMOVED
  }
}
